import type { RouteLocationNormalizedGeneric, RouteRecordRaw } from 'vue-router'
import TemplateCategory from '@/data/models/domain/TemplateCategory'
import type { FacetResolverParams, RedirectableSingleResolverParams } from '@/services/resolvers/types'
import { AppModule } from '@/config/types'
import { CATEGORY_ID_ROUTE_PARAM } from '@/routes/user/const'
import { EmailTemplate } from '@/data/models/domain/EmailTemplate'
import AsyncResolver from '@/components/resolvers/AsyncResolver.vue'
import EmailCampaignSettings from '@/data/models/domain/campaign/EmailCampaignSettings'
import Template from '@/data/models/domain/Template'

export const templateRouteNames = {
  root: 'user.templates',

  allTemplates: 'user.templates.all',
  allTemplatesSms: 'user.templates.all.sms',
  allTemplatesEmail: 'user.templates.all.email',

  categoryTemplates: 'user.templates.category',
  categoryTemplatesSms: 'user.templates.category.sms',
  categoryTemplatesEmail: 'user.templates.category.email',
} as const satisfies Record<string, string>

const getCategoryTableRouteProps = (route: RouteLocationNormalizedGeneric) => {
  const categoryId = route.params[CATEGORY_ID_ROUTE_PARAM]
  return {
    categoryId,
  }
}

const allTemplatesRoute: RouteRecordRaw = {
  name: templateRouteNames.allTemplates,
  path: 'all',
  meta: {
    tabTitle: 'pageTabTitle.user.templates',
    resolvers: [
      {
        service: 'FacetResolver',
        params: {
          model: EmailTemplate,
          subscriptionModels: [EmailTemplate],
          resetSettingsOnSubscription: true,
        } satisfies FacetResolverParams,
      },
      {
        service: 'FacetResolver',
        params: {
          model: Template,
          subscriptionModels: [Template],
          resetSettingsOnSubscription: true,
        } satisfies FacetResolverParams,
      },
    ],
    resolverComponent: AsyncResolver,
  },
  component: () => import('@/components/domain/templates/TemplateContent.vue'),
  redirect: { name: templateRouteNames.allTemplatesSms },
  children: [
    {
      name: templateRouteNames.allTemplatesSms,
      path: 'sms',
      meta: {
        modules: [AppModule.Compose],
      },
      component: () => import('@/components/domain/templates/TemplateSmsTableNew.vue'),
    },
    {
      name: templateRouteNames.allTemplatesEmail,
      path: 'email',
      meta: {
        resolvers: [
          {
            service: 'ComposeEmailResolver',
          },
          {
            service: 'ModelSettingsResolverService',
            params: {
              model: EmailCampaignSettings,
            },
          },
        ],
        modules: [AppModule.Stripo],
      },
      component: () => import('@/components/domain/templates/TemplateEmailTable.vue'),
    },
  ],
}

const categoryTemplatesRoute: RouteRecordRaw = {
  name: templateRouteNames.categoryTemplates,
  path: `categories/:${CATEGORY_ID_ROUTE_PARAM}`,
  meta: {
    tabTitle: 'pageTabTitle.user.templates',
    resolvers: [
      {
        service: 'NonBlockedRedirectableSingleModelResolverService',
        params: {
          requestParam: CATEGORY_ID_ROUTE_PARAM,
          model: TemplateCategory,
          source: 'router',
          redirectToIfNotFound: { name: templateRouteNames.root },
          redirectToIfAccessError: { name: 'user.templates' },
        } satisfies RedirectableSingleResolverParams,
      },
      {
        service: 'FacetResolver',
        params: {
          model: EmailTemplate,
          source: 'router',
          requestParam: CATEGORY_ID_ROUTE_PARAM,
          subscriptionModels: [EmailTemplate],
          resetSettingsOnSubscription: true,
        } satisfies FacetResolverParams,
      },
      {
        service: 'FacetResolver',
        params: {
          model: Template,
          source: 'router',
          requestParam: CATEGORY_ID_ROUTE_PARAM,
          subscriptionModels: [Template],
          resetSettingsOnSubscription: true,
        } satisfies FacetResolverParams,
      },
    ],
  },
  props: (route) => ({
    ...getCategoryTableRouteProps(route),
    tabRoutePrefix: templateRouteNames.categoryTemplates,
  }),
  component: () => import('@/components/domain/templates/TemplateContent.vue'),
  redirect: (route) => ({
    name: templateRouteNames.categoryTemplatesSms,
    params: { [CATEGORY_ID_ROUTE_PARAM]: route.params[CATEGORY_ID_ROUTE_PARAM] },
  }),
  children: [
    {
      name: templateRouteNames.categoryTemplatesSms,
      path: 'sms',
      props: getCategoryTableRouteProps,
      meta: {
        parent: templateRouteNames.categoryTemplates,
        modules: [AppModule.Compose],
      },
      component: () => import('@/components/domain/templates/TemplateSmsTableNew.vue'),
    },
    {
      name: templateRouteNames.categoryTemplatesEmail,
      path: 'email',
      meta: {
        parent: templateRouteNames.categoryTemplates,
        resolvers: [
          {
            service: 'ComposeEmailResolver',
          },
          {
            service: 'ModelSettingsResolverService',
            params: {
              model: EmailCampaignSettings,
            },
          },
        ],
        modules: [AppModule.Stripo],
      },
      props: getCategoryTableRouteProps,
      component: () => import('@/components/domain/templates/TemplateEmailTable.vue'),
    },
  ],
}

export const getTemplatesRoutes = (): RouteRecordRaw[] => [
  {
    name: templateRouteNames.root,
    path: 'templates',
    meta: {
      tags: ['USER_TOP_MENU'],
      modules: [AppModule.Templates, AppModule.ComposeEmail, AppModule.TmCodemirrorEditor],
    },
    props: getCategoryTableRouteProps,
    component: () => import('@/components/views/templates/Templates.vue'),
    redirect: { name: 'user.templates.all' },
    children: [allTemplatesRoute, categoryTemplatesRoute],
  },
]
